import { CHART_BLUE, CHART_COLOR_CYCLE } from '../../theme/theme-colors';
import i18next from 'i18next';
import { CurveType } from '@mui/x-charts';
import '../../i18n';
import { Graph, PythonStreamData, Series } from '../Dashboard/dashboardTypes';
import { FrameworkClassPartType } from '../../framework/framework-enums';
import { FrameworkClasses } from '../../framework/frameworkClasses';
import type { FrameworkClass } from '../../framework';
import { isFrameworkBoolStreamType, isFrameworkFloatStreamType } from '../../utils/micropythonStreamTypes';

const baseSeries = { showMark: false, curve: 'linear' as CurveType | undefined };

export const getTranslation = (key: string, defaultValue: string) => {
  if (!i18next.isInitialized) {
    return defaultValue || key;
  }
  return i18next.t(key, defaultValue);
};

export function getTranslationWithValues(key: string, defaultValue: string, values: Record<string, string | number>): string {
  if (!i18next.isInitialized) {
    return Object.entries(values).reduce(
      (text, [name, value]) => text.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), String(value)),
      defaultValue
    );
  }
  return i18next.t(key, { defaultValue, ...values });
}

/**
 * Maps each DriveBase streamData key to an existing dashboard.chart.title.* locale key
 * (see public/locales).
 */
const CHART_TITLE_I18N_KEY_BY_STREAM_KEY: Record<string, string> = {
  imu: 'dashboard.chart.title.imu',
  accel: 'dashboard.chart.title.accel',
  vel: 'dashboard.chart.title.vel',
  dist: 'dashboard.chart.title.dist',
  pos: 'dashboard.chart.title.pos',
  locator: 'dashboard.chart.title.att.yaw',
};

const CHART_TITLE_DEFAULT_BY_I18N_KEY: Record<string, string> = {
  'dashboard.chart.title.att': 'Orientation (°)',
  'dashboard.chart.title.imu': 'Orientation (°)',
  'dashboard.chart.title.gyro': 'Gyroscope (°/s)',
  'dashboard.chart.title.att.yaw': 'Orientation Yaw (°)',
  'dashboard.chart.title.accel': 'Accelerometer (g)',
  'dashboard.chart.title.vel': 'Velocity (m/s)',
  'dashboard.chart.title.dist': 'Distance (cm)',
  'dashboard.chart.title.pos': 'Position (cm)',
};

/** For tests: i18n key under dashboard.chart.title for a stream graph. */
export function chartTitleI18nKeyForStreamKey(streamKey: string): string {
  return CHART_TITLE_I18N_KEY_BY_STREAM_KEY[streamKey] ?? `dashboard.chart.title.${streamKey}`;
}

/** Stable pitch/roll/yaw i18n keys for gyro widget titles and chart series buttons. */
const GYRO_AXIS_I18N = {
  pitch: {
    chartTitle: 'dashboard.chart.title.gyro.pitch',
    chartButton: 'chart.button.gyro.pitch',
  },
  roll: {
    chartTitle: 'dashboard.chart.title.gyro.roll',
    chartButton: 'chart.button.gyro.roll',
  },
  yaw: {
    chartTitle: 'dashboard.chart.title.gyro.yaw',
    chartButton: 'chart.button.gyro.yaw',
  },
} as const;

type GyroAxis = keyof typeof GYRO_AXIS_I18N;

/** Maps gyro stream/data keys to pitch/roll/yaw (widget titles and Gyro X/Y/Z chart buttons). */
const GYRO_DATA_KEY_TO_AXIS: Record<string, GyroAxis> = {
  Pitch: 'pitch',
  Roll: 'roll',
  Yaw: 'yaw',
  'Gyro X': 'pitch',
  'Gyro Y': 'roll',
  'Gyro Z': 'yaw',
};

const GYRO_AXIS_TO_STREAM_DATA_KEY: Record<GyroAxis, string> = {
  pitch: 'Gyro X',
  roll: 'Gyro Y',
  yaw: 'Gyro Z',
};

/**
 * Resolves a drive-base widget layout `dataKey` to the framework stream name used in
 * `DataStreamStore.set_request_list` and graph series (e.g. gyro `Pitch` → `Gyro X`).
 */
export function drivebaseWidgetStreamDataKey(widgetType: string, dataKey: string): string {
  if (widgetType !== 'gyro' || !dataKey || dataKey === 'all') return dataKey;
  const axis = GYRO_DATA_KEY_TO_AXIS[dataKey];
  if (axis) return GYRO_AXIS_TO_STREAM_DATA_KEY[axis];
  return dataKey;
}

/** Chart header title key for a dashboard graph widget from layout `type` / `dataKey`. */
export function widgetChartTitleI18nKey(widgetType: string, dataKey: string): string {
  if (dataKey === 'all') return chartTitleI18nKeyForStreamKey(widgetType);
  if (widgetType === 'gyro') {
    const axis = GYRO_DATA_KEY_TO_AXIS[dataKey];
    if (axis) return GYRO_AXIS_I18N[axis].chartTitle;
  }
  return `dashboard.chart.title.${widgetType}.${dataKey}`;
}

function chartTitleForStreamKey(streamKey: string): string {
  const i18nKey = chartTitleI18nKeyForStreamKey(streamKey);
  const defaultText = CHART_TITLE_DEFAULT_BY_I18N_KEY[i18nKey] ?? streamKey;
  return getTranslation(i18nKey, defaultText);
}

export const MOTOR_WIDGET_TYPE = 'motor';
/** Multi-metric line chart for one drive-unit motor port (velocity + current, etc.). */
export const MOTOR_MULTI_WIDGET_TYPE = 'motor_multi';
/** Single-metric line chart for a non-drivebase mechanism (e.g. RotationalMechanism, LinearMechanism). */
export const MECHANISM_WIDGET_TYPE = 'mechanism_data';
export const SENSOR_WIDGET_TYPE = 'sensor';
export const OTHER_WIDGET_TYPE = 'other';
export const BOOLEAN_WIDGET_TYPE = 'boolean';
/** Line graph widget for a user-registered DataStreamStore callable. */
export const CUSTOM_CALLABLE_WIDGET_TYPE = 'custom_callable';

const MOTOR_MULTI_WIDGET_DATA_KEY_RE = /^(motor|mechanism)\.port(\d+)$/;

/** Default English copy for motor boolean metric i18n keys when Locize is unavailable. */
const MOTOR_BOOLEAN_METRIC_DEFAULTS: Record<string, string> = {
  is_at_target: 'Target Position',
};

const MOTOR_STREAM_KEY_RE = /^(?:motor|mechanism)\.port\d+\./;
const FRAMEWORK_SENSOR_STREAM_TEMPLATE_RE = /^([^.]+)\.port\{port\}\./;

function buildElectronicSensorStreamPrefixToClass(): ReadonlyMap<string, string> {
  const byPrefix = new Map<string, string>();
  for (const frameworkClass of FrameworkClasses) {
    if (frameworkClass.partType !== FrameworkClassPartType.Sensors) continue;
    for (const entry of frameworkClass.streamData ?? []) {
      const match = entry.name.match(FRAMEWORK_SENSOR_STREAM_TEMPLATE_RE);
      if (!match) continue;
      byPrefix.set(match[1], frameworkClass.name);
    }
  }
  return byPrefix;
}

const ELECTRONIC_SENSOR_STREAM_PREFIX_TO_CLASS = buildElectronicSensorStreamPrefixToClass();

function buildElectronicOtherStreamPrefixToClass(): ReadonlyMap<string, string> {
  const byPrefix = new Map<string, string>();
  for (const frameworkClass of FrameworkClasses) {
    if (frameworkClass.partType !== FrameworkClassPartType.Other) continue;
    for (const entry of frameworkClass.streamData ?? []) {
      const match = entry.name.match(FRAMEWORK_SENSOR_STREAM_TEMPLATE_RE);
      if (!match) continue;
      byPrefix.set(match[1], frameworkClass.name);
    }
  }
  return byPrefix;
}

const ELECTRONIC_OTHER_STREAM_PREFIX_TO_CLASS = buildElectronicOtherStreamPrefixToClass();

/** Substitutes `{port}` and `{motor_port}` placeholders in framework `streamData` names. */
export function resolveFrameworkStreamSensorKeys(frameworkClass: FrameworkClass | undefined, port: number): string[] {
  if (!frameworkClass?.streamData?.length) return [];
  const portToken = String(port);
  return frameworkClass.streamData.map((entry) => entry.name.replace(/\{motor_port\}/g, portToken).replace(/\{port\}/g, portToken));
}

/** Resolved stream keys for float `streamData` entries only (motor/mechanism widgets). */
export function resolveFrameworkFloatStreamSensorKeys(frameworkClass: FrameworkClass | undefined, port: number): string[] {
  if (!frameworkClass?.streamData?.length) return [];
  const portToken = String(port);
  return frameworkClass.streamData
    .filter((entry) => isFrameworkFloatStreamType(entry.type))
    .map((entry) => entry.name.replace(/\{motor_port\}/g, portToken).replace(/\{port\}/g, portToken));
}

/** Resolved stream keys for bool `streamData` entries (motor/sensor booleans). */
export function resolveFrameworkBoolStreamSensorKeys(frameworkClass: FrameworkClass | undefined, port: number): string[] {
  if (!frameworkClass?.streamData?.length) return [];
  const portToken = String(port);
  return frameworkClass.streamData
    .filter((entry) => isFrameworkBoolStreamType(entry.type))
    .map((entry) => entry.name.replace(/\{motor_port\}/g, portToken).replace(/\{port\}/g, portToken));
}

/** Localized motor boolean metric title (collapsible drawer headers and widget titles). */
export function motorBooleanMetricLabel(metric: string): string {
  return getTranslation(motorDraggableTitleI18nKey(metric), MOTOR_BOOLEAN_METRIC_DEFAULTS[metric] ?? metric);
}

/** Collapsible motor-data drawer header for a float metric (all ports). */
export function motorDrawerFloatMetricGroupTitle(metric: string): string {
  return motorMetricDisplayTitle(metric, '');
}

/** Dashboard motor float widget title: optional per-port label plus drawer group metric title. */
export function motorFloatWidgetDisplayTitle(portLabel: string | undefined, metric: string): string {
  const metricTitle = motorDrawerFloatMetricGroupTitle(metric);
  const label = portLabel?.trim();
  return label ? `${label} ${metricTitle}` : metricTitle;
}

/** Collapsible motor-data drawer header for a boolean metric (all ports). */
export function motorDrawerBooleanMetricGroupTitle(metric: string): string {
  return motorBooleanMetricLabel(metric);
}

/** Dashboard motor boolean widget title: optional per-port label plus drawer group metric title. */
export function motorBooleanWidgetDisplayTitle(portLabel: string | undefined, metric: string): string {
  const metricTitle = motorDrawerBooleanMetricGroupTitle(metric);
  const label = portLabel?.trim();
  return label ? `${label} ${metricTitle}` : metricTitle;
}

/** Single-port motor boolean widget header: port label plus which boolean metric is shown. */
export function motorBooleanSinglePortWidgetTitle(portLabel: string, metric: string): string {
  return motorBooleanWidgetDisplayTitle(portLabel, metric);
}

export function mechanismDraggableTitleI18nKey(className: string, metric: string): string {
  return `dashboard.draggableComponent.title.${className}.${metric}`;
}

/** Collapsible mechanism-data drawer header for a float metric (all instances). */
export function mechanismDrawerMetricGroupTitle(
  mechanismClassName: string,
  metric: string,
  translate: (key: string, defaultValue: string) => string,
  deviceTypeLabel?: string
): string {
  return translate(mechanismDraggableTitleI18nKey(mechanismClassName, metric), `${deviceTypeLabel ?? mechanismClassName} ${metric}`);
}

/** Dashboard mechanism float widget title: optional per-port label plus drawer group metric title. */
export function mechanismFloatWidgetDisplayTitle(
  portLabel: string | undefined,
  mechanismClassName: string,
  metric: string,
  translate: (key: string, defaultValue: string) => string,
  deviceTypeLabel?: string
): string {
  const metricTitle = mechanismDrawerMetricGroupTitle(mechanismClassName, metric, translate, deviceTypeLabel);
  const label = portLabel?.trim();
  return label ? `${label} ${metricTitle}` : metricTitle;
}

/** Per-port motor/mechanism label in the motor-data drawer (collapsed metric rows). */
export function motorDrawerPortVariableTitle(port: number, variableName: string): string {
  if (!variableName.trim()) {
    return getTranslationWithValues('dashboard.draggableComponent.title.portOnly', 'Port {{port}}', { port });
  }
  return getTranslationWithValues('dashboard.draggableComponent.title.portVariable', 'Port {{port}}: {{variableName}}', {
    port,
    variableName,
  });
}

/** Port label for a standalone motor (choose motors, drawer draggables, chart buttons). */
export function motorPortDisplayTitle(port: number, variableName: string): string {
  if (!variableName.trim()) {
    return getTranslationWithValues('dashboard.motor.portOnlyLabel', 'Port {{port}}', { port });
  }
  return getTranslationWithValues('dashboard.motor.portLabel', 'Port {{port}}: {{variableName}}', {
    port,
    variableName,
  });
}

/** Port label for a mechanism's embedded lead motor (choose motors, drawer draggables, chart buttons). */
export function motorLeadMotorPortDisplayTitle(port: number, mechanismVariableName: string): string {
  return getTranslationWithValues('dashboard.motor.leadMotorPortLabel', 'Port {{port}}: {{variableName}} - Lead Motor', {
    port,
    variableName: mechanismVariableName,
  });
}

/** Port label for a mechanism follower motor; `motorName` is user-authored code (not localized). */
export function motorFollowerMotorPortDisplayTitle(port: number, mechanismVariableName: string, motorName: string): string {
  return getTranslationWithValues('dashboard.motor.followerMotorPortLabel', 'Port {{port}}: {{variableName}} - {{motorName}}', {
    port,
    variableName: mechanismVariableName,
    motorName,
  });
}

export function isMotorStreamSensorKey(sensorKey: string): boolean {
  return MOTOR_STREAM_KEY_RE.test(sensorKey);
}

export function isElectronicSensorStreamSensorKey(sensorKey: string): boolean {
  const prefix = sensorKey.split('.')[0] ?? '';
  if (!ELECTRONIC_SENSOR_STREAM_PREFIX_TO_CLASS.has(prefix)) return false;
  return /\.port\d+\./.test(sensorKey);
}

/** Framework class name for an electronic sensor stream key (`beam.port1.*`, etc.). */
export function sensorClassNameFromStreamKey(sensorKey: string): string | null {
  if (!isElectronicSensorStreamSensorKey(sensorKey)) return null;
  const prefix = sensorKey.split('.')[0] ?? '';
  return ELECTRONIC_SENSOR_STREAM_PREFIX_TO_CLASS.get(prefix) ?? null;
}

export function isOtherElectronicsStreamSensorKey(sensorKey: string): boolean {
  const prefix = sensorKey.split('.')[0] ?? '';
  if (!ELECTRONIC_OTHER_STREAM_PREFIX_TO_CLASS.has(prefix)) return false;
  return /\.port\d+\./.test(sensorKey);
}

/** Framework class name for an other-electronics stream key (`bit_adapter.port1.*`, etc.). */
export function otherClassNameFromStreamKey(sensorKey: string): string | null {
  if (!isOtherElectronicsStreamSensorKey(sensorKey)) return null;
  const prefix = sensorKey.split('.')[0] ?? '';
  return ELECTRONIC_OTHER_STREAM_PREFIX_TO_CLASS.get(prefix) ?? null;
}

/** Metric segment after the port in a motor/mechanism stream key (e.g. `velocity`). */
export function streamMetricSlug(sensorKey: string): string {
  const parts = sensorKey.split('.');
  return parts[parts.length - 1] ?? sensorKey;
}

/** Stream prefix before `.portN.` (e.g. `beam` from `beam.port1.detection_range`). */
export function streamPrefixFromSensorKey(sensorKey: string): string {
  return sensorKey.split('.')[0] ?? sensorKey;
}

export function streamPortFromSensorKey(sensorKey: string): number | null {
  const match = sensorKey.match(/\.port(\d+)\./);
  if (!match) return null;
  const port = Number.parseInt(match[1], 10);
  return Number.isFinite(port) ? port : null;
}

/**
 * Removes the drive-unit port segment so i18n keys stay stable across ports
 * (e.g. `motor.port1.temperature` → `motor.temperature`).
 */
export function streamKeyToStableI18nSlug(sensorKey: string): string {
  return sensorKey.replace(/\.port\d+\./, '.');
}

/** Locize key for CSV export checkboxes — port is passed via `{{port}}`, not in the key. */
export function sensorCheckboxI18nKey(sensorKey: string): string {
  return `sensor.checkbox.${streamKeyToStableI18nSlug(sensorKey)}`;
}

/** True when an i18n key incorrectly embeds a port number (e.g. `sensor.checkbox.motor.port1.temperature`). */
export function i18nKeyEmbedsPortSegment(key: string): boolean {
  return /\.port\d+(\.|$)/.test(key);
}

export function motorDraggableTitleI18nKey(metric: string): string {
  return `dashboard.draggableComponent.title.motor.${metric}`;
}

export function motorChartTitleI18nKey(metric: string): string {
  return `dashboard.chart.title.motor.${metric}`;
}

export function mechanismChartTitleI18nKey(className: string, metric: string): string {
  return `dashboard.chart.title.${className}.${metric}`;
}

export function motorChartButtonI18nKey(metric: string): string {
  return `chart.button.motor.${metric}`;
}

export function motorDraggableTitleDefault(metric: string, port: number | string): string {
  return `Motor Port ${port} ${metric}`;
}

export function motorChartTitleDefault(metric: string, port: number | string): string {
  return motorDraggableTitleDefault(metric, port);
}

/** Motor metric label using existing draggable/chart i18n keys with `{{port}}` interpolation. */
export function motorMetricDisplayTitle(metric: string, port: number | string): string {
  return getTranslationWithValues(motorDraggableTitleI18nKey(metric), motorDraggableTitleDefault(metric, port), { port });
}

/**
 * Motor widget/draggable label with port from project code.
 * Use this for display until Locize entries include `{{port}}` on keys like
 * `dashboard.draggableComponent.title.motor.{metric}` (shared per-metric keys otherwise
 * resolve to one static port in cached translations).
 */
export function motorWidgetDisplayTitle(metric: string, port: number): string {
  return motorDraggableTitleDefault(metric, port);
}

/** Port-only anchor key for multi-metric motor charts (`motor.port3`). */
export function motorMultiWidgetDataKey(port: number, prefix: string = 'motor'): string {
  return `${prefix}.port${port}`;
}

export function parseMotorMultiWidgetDataKey(dataKey: string): { prefix: string; port: number } | null {
  const match = dataKey.match(MOTOR_MULTI_WIDGET_DATA_KEY_RE);
  if (!match?.[1] || !match[2]) return null;
  const port = Number.parseInt(match[2], 10);
  if (!Number.isFinite(port)) return null;
  return { prefix: match[1], port };
}

export function isMotorMultiWidgetDataKey(dataKey: string): boolean {
  return MOTOR_MULTI_WIDGET_DATA_KEY_RE.test(dataKey);
}

/** Non-localized drawer/widget title for a port-only motor multi chart. */
export function motorMultiWidgetDisplayTitle(port: number): string {
  return `Motor Port ${port}`;
}

/** Default float metric when a multi-metric motor chart is first added. */
export const MOTOR_MULTI_DEFAULT_METRIC = 'velocity';

/** Maximum float streams on a multi-axis motor/mechanism chart. */
export const MULTI_AXIS_MAX_METRICS = 4;

/** Preferred default motor metrics (stream slugs) for new multi-axis charts, in display order. */
export const MOTOR_MULTI_PREFERRED_DEFAULT_METRICS = ['velocity', 'position', 'acceleration', 'current'] as const;

/** Full stream keys for one port and a list of metric slugs. */
export function motorStreamKeysForPortAndMetrics(port: number, prefix: string, metrics: readonly string[]): string[] {
  const uniqueMetrics = [...new Set(metrics)].filter((metric) => metric.length > 0);
  return uniqueMetrics.map((metric) => `${prefix}.port${port}.${metric}`);
}

export function sensorDraggableTitleI18nKey(prefix: string, metric: string): string {
  return `dashboard.draggableComponent.title.sensor.${prefix}.${metric}`;
}

export function sensorChartTitleI18nKey(prefix: string, metric: string): string {
  return `dashboard.chart.title.sensor.${prefix}.${metric}`;
}

export function sensorChartButtonI18nKey(prefix: string, metric: string): string {
  return `chart.button.sensor.${prefix}.${metric}`;
}

export function sensorDraggableTitleDefault(metric: string, port: number): string {
  return `Sensor Port ${port} ${metric}`;
}

export function sensorChartTitleDefault(metric: string, port: number): string {
  return sensorDraggableTitleDefault(metric, port);
}

function sensorMetricLabelDefault(prefix: string, metric: string): string {
  return `${prefix}.${metric}`;
}

/** Localized sensor metric title for drawer group headers and multi-port widget titles. */
export function sensorMetricLabel(prefix: string, metric: string): string {
  return getTranslation(sensorChartTitleI18nKey(prefix, metric), sensorMetricLabelDefault(prefix, metric));
}

/** Dashboard sensor widget title: optional per-port label plus drawer group metric title. */
export function sensorWidgetMetricDisplayTitle(portLabel: string | undefined, prefix: string, metric: string): string {
  const metricTitle = sensorMetricLabel(prefix, metric);
  const label = portLabel?.trim();
  return label ? `${label} ${metricTitle}` : metricTitle;
}

/** Per-port sensor float graph title with port interpolation. */
export function sensorWidgetDisplayTitle(sensorKey: string): string {
  const prefix = streamPrefixFromSensorKey(sensorKey);
  const metric = streamMetricSlug(sensorKey);
  const port = streamPortFromSensorKey(sensorKey) ?? 0;
  return getTranslationWithValues(sensorChartTitleI18nKey(prefix, metric), `Sensor Port {{port}} ${metric}`, { port });
}

const SENSOR_DRAWER_SECTION_TITLE_I18N: Partial<Record<string, { key: string; defaultValue: string }>> = {
  BeamBreakSensor: { key: 'electronicExplorer.electronic.beam-break-sensor', defaultValue: 'Beam Break Sensor' },
  DistanceSensor: { key: 'electronicExplorer.electronic.distance-sensor', defaultValue: 'Distance Sensor' },
  MagneticProximitySwitch: { key: 'electronicExplorer.electronic.magnetic-switch', defaultValue: 'Magnetic Proximity Switch' },
};

/** Top-level sensor-data drawer section title for a framework sensor class. */
export function sensorDrawerSectionTitle(
  sensorClassName: string,
  translate: (key: string, defaultValue: string) => string = (_key, defaultValue) => defaultValue
): string {
  const entry = SENSOR_DRAWER_SECTION_TITLE_I18N[sensorClassName];
  if (!entry) return sensorClassName;
  return translate(entry.key, entry.defaultValue);
}

export function otherDraggableTitleI18nKey(prefix: string, metric: string): string {
  return `dashboard.draggableComponent.title.other.${prefix}.${metric}`;
}

export function otherChartTitleI18nKey(prefix: string, metric: string): string {
  return `dashboard.chart.title.other.${prefix}.${metric}`;
}

export function otherChartButtonI18nKey(prefix: string, metric: string): string {
  return `chart.button.other.${prefix}.${metric}`;
}

/**
 * Stable `chart.button.*` key for a port-based stream key — port is not part of the key path.
 * Drive-base series keys (e.g. `Pitch`, `Gyro X`) pass through unchanged except gyro axes map to
 * `chart.button.gyro.{pitch,roll,yaw}` so labels read Pitch/Roll/Yaw instead of Gyro X/Y/Z.
 */
export function chartButtonI18nKeyForStreamKey(streamKey: string): string {
  if (isMotorStreamSensorKey(streamKey)) {
    return motorChartButtonI18nKey(streamMetricSlug(streamKey));
  }
  if (isElectronicSensorStreamSensorKey(streamKey)) {
    return sensorChartButtonI18nKey(streamPrefixFromSensorKey(streamKey), streamMetricSlug(streamKey));
  }
  if (isOtherElectronicsStreamSensorKey(streamKey)) {
    return otherChartButtonI18nKey(streamPrefixFromSensorKey(streamKey), streamMetricSlug(streamKey));
  }
  const gyroAxis = GYRO_DATA_KEY_TO_AXIS[streamKey];
  if (gyroAxis && streamKey.startsWith('Gyro ')) {
    return GYRO_AXIS_I18N[gyroAxis].chartButton;
  }
  return `chart.button.${streamKey}`;
}

function otherMetricLabelDefault(prefix: string, metric: string): string {
  return `${prefix}.${metric}`;
}

/** Localized other-electronics metric title for drawer group headers and multi-port widget titles. */
export function otherMetricLabel(prefix: string, metric: string): string {
  return getTranslation(otherChartTitleI18nKey(prefix, metric), otherMetricLabelDefault(prefix, metric));
}

/** Dashboard other-electronics widget title: optional per-port label plus drawer group metric title. */
export function otherWidgetMetricDisplayTitle(portLabel: string | undefined, prefix: string, metric: string): string {
  const metricTitle = otherMetricLabel(prefix, metric);
  const label = portLabel?.trim();
  return label ? `${label} ${metricTitle}` : metricTitle;
}

const OTHER_DRAWER_SECTION_TITLE_I18N: Partial<Record<string, { key: string; defaultValue: string }>> = {
  BitAdapter: { key: 'electronicExplorer.electronic.BPE-adaptor', defaultValue: 'Bit Adapter' },
};

/** Top-level other-data drawer section title for a framework other-electronics class. */
export function otherDrawerSectionTitle(
  otherClassName: string,
  translate: (key: string, defaultValue: string) => string = (_key, defaultValue) => defaultValue
): string {
  const entry = OTHER_DRAWER_SECTION_TITLE_I18N[otherClassName];
  if (!entry) return otherClassName;
  return translate(entry.key, entry.defaultValue);
}

/** Per-port other-electronics graph title from a stream key — metric only; port belongs in per-instance labels. */
export function otherWidgetDisplayTitle(sensorKey: string): string {
  const prefix = streamPrefixFromSensorKey(sensorKey);
  const metric = streamMetricSlug(sensorKey);
  return otherMetricLabel(prefix, metric);
}

/**
 * Dashboard chart family for electronics plugged into drive-unit ports (`motor_port` / `sensor_port`).
 * Not the DriveBase itself (IMU, velocity, etc. use `SENSOR_OBJECTS`).
 */
export type ElectronicsPortStreamKind = typeof MOTOR_WIDGET_TYPE | typeof SENSOR_WIDGET_TYPE | typeof OTHER_WIDGET_TYPE;

function electronicsPortStreamWidgetTitle(sensorKey: string, kind: ElectronicsPortStreamKind): string {
  if (kind === MOTOR_WIDGET_TYPE) {
    const metric = streamMetricSlug(sensorKey);
    const port = streamPortFromSensorKey(sensorKey) ?? 0;
    return motorWidgetDisplayTitle(metric, port);
  }
  if (kind === SENSOR_WIDGET_TYPE) return sensorWidgetDisplayTitle(sensorKey);
  return otherWidgetDisplayTitle(sensorKey);
}

/** Single-series graph for one motor or sensor port stream key (`graph.key` === full stream key). */
export function buildElectronicsPortStreamGraph(sensorKey: string, kind: ElectronicsPortStreamKind): Graph {
  return {
    key: sensorKey,
    title: electronicsPortStreamWidgetTitle(sensorKey, kind),
    series: [
      {
        ...baseSeries,
        dataKey: sensorKey,
        color: CHART_BLUE,
      },
    ],
    data: [],
  };
}

/** Merges per-key motor/sensor port graphs into one chart with a series per selected stream key. */
export function mergeElectronicsPortStreamGraphs(
  sourceGraphs: Graph[],
  sensorKeys: readonly string[],
  kind: ElectronicsPortStreamKind,
  curve?: CurveType
): Graph | undefined {
  const uniqueSensorKeys: string[] = [];
  for (const key of sensorKeys) {
    if (!uniqueSensorKeys.includes(key)) uniqueSensorKeys.push(key);
  }
  if (!uniqueSensorKeys.length) return undefined;

  const graphsByKey = new Map(sourceGraphs.map((graph) => [graph.key, graph]));
  const series: Series[] = [];
  const dataByTime = new Map<number, PythonStreamData>();

  uniqueSensorKeys.forEach((sensorKey, index) => {
    const source = graphsByKey.get(sensorKey);
    series.push({
      ...baseSeries,
      dataKey: sensorKey,
      color: CHART_COLOR_CYCLE[index % CHART_COLOR_CYCLE.length],
      curve,
    });
    for (const row of source?.data ?? []) {
      const time = row.time;
      if (!isFiniteStreamNumber(time)) continue;
      const merged = dataByTime.get(time) ?? { time };
      if (isFiniteStreamNumber(row[sensorKey])) {
        merged[sensorKey] = row[sensorKey];
      } else if (row[sensorKey] === null) {
        merged[sensorKey] = null;
      }
      dataByTime.set(time, merged);
    }
  });

  return {
    key: uniqueSensorKeys[0],
    title: electronicsPortStreamWidgetTitle(uniqueSensorKeys[0], kind),
    series,
    data: [...dataByTime.values()].sort((a, b) => a.time - b.time),
  };
}

function groupDriveBaseStreamDataByKey(
  streamData: NonNullable<FrameworkClass['streamData']> | undefined
): Map<string, NonNullable<FrameworkClass['streamData']>> {
  const grouped = new Map<string, NonNullable<FrameworkClass['streamData']>>();
  for (const entry of streamData ?? []) {
    if (!entry.key) continue;
    const list = grouped.get(entry.key) ?? [];
    list.push(entry);
    grouped.set(entry.key, list);
  }
  return grouped;
}

function buildGraphsFromDriveBaseStreamKeys(): { [key: string]: Graph } {
  const driveBase = FrameworkClasses.find((c) => c.name === 'DriveBase');
  const grouped = groupDriveBaseStreamDataByKey(driveBase?.streamData);
  const graphs: { [key: string]: Graph } = {};

  for (const [graphKey, entries] of grouped) {
    const series: Series[] = entries.map((entry, i) => ({
      ...baseSeries,
      dataKey: entry.name,
      color: CHART_COLOR_CYCLE[i % CHART_COLOR_CYCLE.length],
    }));
    graphs[graphKey] = {
      key: graphKey,
      title: chartTitleForStreamKey(graphKey),
      series,
      data: [],
    };
  }

  return graphs;
}

export const SENSOR_OBJECTS: { [key: string]: Graph } = buildGraphsFromDriveBaseStreamKeys();

export const EMPTY_SENSOR_OBJECTS: { [key: string]: Graph } = (() => {
  const empty: { [key: string]: Graph } = {};
  for (const [k, g] of Object.entries(SENSOR_OBJECTS)) {
    empty[k] = { ...g, series: [...g.series], data: [] };
  }
  return empty;
})();

/** Two-point placeholder (time=0, time=1) for empty charts to display axes with a flat line. */
export const EMPTY_CHART_PLACEHOLDER: Array<{ time: number; pythonStream: { time: number } }> = [
  { time: 0, pythonStream: { time: 0 } },
  { time: 1, pythonStream: { time: 1 } },
];

export const getSensorDataObjects = () => {
  const fresh: { [key: string]: Graph } = {};
  for (const [k, g] of Object.entries(SENSOR_OBJECTS)) {
    fresh[k] = { ...g, series: [...g.series], data: [] };
  }
  return fresh;
};

/** Drivebase graph bucket keys (`imu`, `vel`, …) whose series intersect `streamKeys`. */
export function drivebaseGraphKeysForStreamKeys(streamKeys: readonly string[]): ReadonlySet<string> {
  const keySet = new Set(streamKeys);
  const graphKeys = new Set<string>();
  for (const [graphKey, graph] of Object.entries(SENSOR_OBJECTS)) {
    if (graph.series.some((series) => keySet.has(series.dataKey))) {
      graphKeys.add(graphKey);
    }
  }
  return graphKeys;
}

/** True when a stream key is DriveBase / IMU data (graph bucket or series `dataKey`). */
export function isDrivebaseCsvStreamKey(dataKey: string): boolean {
  if (Object.prototype.hasOwnProperty.call(SENSOR_OBJECTS, dataKey)) return true;
  for (const graph of Object.values(SENSOR_OBJECTS)) {
    if (graph.series.some((series) => series.dataKey === dataKey)) return true;
  }
  return false;
}

/** Live chart display window in seconds (display only; full run remains in activeRun / IndexedDB). */
export const LIVE_GRAPH_WINDOW_SECONDS = 60;

/** Row cap when stream rows lack finite `time` (display only; ~60s at 10 Hz). */
export const LIVE_GRAPH_FALLBACK_MAX_ROWS = 600;

/** Max rows shown in raw-data widgets during a live run. */
export const LIVE_RAW_DATA_MAX_ROWS = 100;

/** Keeps the most recent rows for raw-data table display (display only). */
export function sliceGraphDataForRawDisplay<T>(data: readonly T[], maxRows = LIVE_RAW_DATA_MAX_ROWS): T[] {
  if (data.length <= maxRows) return [...data];
  return data.slice(-maxRows);
}

/** Trims graph rows to the live sliding time window for chart publish (does not mutate ingest buffers). */
export function sliceGraphDataForLiveDisplay<T extends { time?: number }>(data: readonly T[]): T[] {
  if (data.length === 0) return [];
  let lastTime: number | undefined;
  for (let i = data.length - 1; i >= 0; i--) {
    const time = data[i]?.time;
    if (typeof time === 'number' && Number.isFinite(time)) {
      lastTime = time;
      break;
    }
  }
  if (lastTime === undefined) {
    if (data.length <= LIVE_GRAPH_FALLBACK_MAX_ROWS) return [...data];
    return data.slice(-LIVE_GRAPH_FALLBACK_MAX_ROWS);
  }
  const minTime = lastTime - LIVE_GRAPH_WINDOW_SECONDS;
  let startIndex = 0;
  for (let i = 0; i < data.length; i++) {
    const t = data[i]?.time;
    if (typeof t === 'number' && Number.isFinite(t) && t >= minTime) {
      startIndex = i;
      break;
    }
  }
  return data.slice(startIndex);
}

/** Immutable graph snapshot with live display window applied to `data`. */
export function snapshotGraphForLiveDisplay(graph: Graph): Graph {
  return {
    ...graph,
    series: [...graph.series],
    data: sliceGraphDataForLiveDisplay(graph.data),
  };
}

/** Builds a live publish snapshot from in-memory graph refs (scoped drivebase + port/custom streams). */
export function buildLiveGraphSnapshot(opts: {
  sensorObjects: Record<string, Graph>;
  activeDrivebaseGraphKeys: ReadonlySet<string>;
  electronicsPortStreamGraphs: Record<string, Graph>;
  customCallableStreamGraphs: Record<string, Graph>;
}): Graph[] {
  const drivebaseGraphs = [...opts.activeDrivebaseGraphKeys]
    .map((graphKey) => opts.sensorObjects[graphKey])
    .filter((graph): graph is Graph => graph != null)
    .map(snapshotGraphForLiveDisplay);

  const electronicsGraphs = snapshotElectronicsPortStreamGraphs(opts.electronicsPortStreamGraphs).map(snapshotGraphForLiveDisplay);
  const customCallableGraphs = snapshotElectronicsPortStreamGraphs(opts.customCallableStreamGraphs).map(snapshotGraphForLiveDisplay);

  return [...drivebaseGraphs, ...electronicsGraphs, ...customCallableGraphs];
}

/** Appends a stream sample only to drivebase graphs required by the active layout. */
export function appendPythonStreamToScopedDrivebaseGraphs(
  sensorObjects: Record<string, Graph>,
  sample: PythonStreamData,
  activeGraphKeys: ReadonlySet<string>
): void {
  for (const graphKey of activeGraphKeys) {
    const graph = sensorObjects[graphKey];
    if (graph) {
      appendPythonStreamSampleToGraph(graph, sample);
    }
  }
}

function isFiniteStreamNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isExplicitInvalidStreamValue(value: unknown): boolean {
  return value == null || (typeof value === 'number' && !Number.isFinite(value));
}

/**
 * Returns the next accepted sample time when it is finite and monotonic, otherwise `null`.
 * Callers own the previous-time cursor so live ingest and replay cannot interfere.
 */
export function getAcceptedStreamSampleTime(previousTime: number | null, sample: PythonStreamData): number | null {
  const time = sample.time;
  if (!isFiniteStreamNumber(time)) return null;
  if (previousTime != null && time + 1e-9 < previousTime) return null;
  return time;
}

/**
 * Builds one chart row from a full stream sample: `time` plus only `series.dataKey` fields that
 * are present on the sample. Valid readings are copied as numbers; explicit invalid readings are
 * copied as null so MUI line charts render a gap. Returns null when nothing in this sample belongs
 * on the chart (avoids duplicating the full stream into every graph's buffer).
 */
export function projectPythonStreamSampleForGraph(graph: Graph, sample: PythonStreamData): PythonStreamData | null {
  if (!graph.series?.length) return null;

  const time = sample.time;
  if (!isFiniteStreamNumber(time)) return null;

  const row: PythonStreamData = { time };
  for (const s of graph.series) {
    const k = s.dataKey;
    if (k === 'time') continue;
    if (!Object.prototype.hasOwnProperty.call(sample, k)) continue;
    const v = sample[k];
    if (isFiniteStreamNumber(v)) {
      row[k] = v;
    } else if (isExplicitInvalidStreamValue(v)) {
      row[k] = null;
    }
  }

  const measuredKeys = Object.keys(row).filter((key) => key !== 'time');
  if (!measuredKeys.length) return null;
  return row;
}

function canAppendMonotonicTime(graph: Graph, time: number): boolean {
  const last = graph.data.at(-1)?.time;
  if (!isFiniteStreamNumber(last)) return true;
  return !(time + 1e-9 < last);
}

/** Appends one projected stream row to a graph buffer, enforcing monotonic time (non-decreasing). */
export function appendPythonStreamSampleToGraph(graph: Graph, sample: PythonStreamData): boolean {
  const row = projectPythonStreamSampleForGraph(graph, sample);
  if (!row) return false;
  if (!canAppendMonotonicTime(graph, row.time)) return false;

  graph.data.push(row);
  return true;
}

export function appendElectronicsPortStreamSampleToGraphs(
  streamGraphs: Record<string, Graph>,
  sample: PythonStreamData,
  streamKeys: readonly string[],
  kind: ElectronicsPortStreamKind
): void {
  for (const sensorKey of streamKeys) {
    if (!streamGraphs[sensorKey]) {
      streamGraphs[sensorKey] = buildElectronicsPortStreamGraph(sensorKey, kind);
    }
    const row = projectPythonStreamSampleForGraph(streamGraphs[sensorKey], sample);
    if (!row) continue;
    if (!canAppendMonotonicTime(streamGraphs[sensorKey], row.time)) continue;
    streamGraphs[sensorKey].data.push(row);
  }
}

export function snapshotElectronicsPortStreamGraphs(streamGraphs: Record<string, Graph>): Graph[] {
  return Object.values(streamGraphs).map((graph) => ({
    ...graph,
    series: [...graph.series],
    data: [...graph.data],
  }));
}

/** Single-series graph for a user-registered DataStreamStore callable (`graph.key` === callable name). */
export function buildCustomCallableStreamGraph(streamKey: string): Graph {
  return {
    key: streamKey,
    title: streamKey,
    series: [{ ...baseSeries, dataKey: streamKey, color: CHART_BLUE }],
    data: [],
  };
}

/** Appends live stream samples into per-callable graph buffers (mirrors `appendElectronicsPortStreamSampleToGraphs`). */
export function appendCustomCallableStreamSampleToGraphs(
  streamGraphs: Record<string, Graph>,
  sample: PythonStreamData,
  streamKeys: readonly string[]
): void {
  for (const streamKey of streamKeys) {
    if (!streamGraphs[streamKey]) {
      streamGraphs[streamKey] = buildCustomCallableStreamGraph(streamKey);
    }
    const row = projectPythonStreamSampleForGraph(streamGraphs[streamKey], sample);
    if (!row) continue;
    if (!canAppendMonotonicTime(streamGraphs[streamKey], row.time)) continue;
    streamGraphs[streamKey].data.push(row);
  }
}

export const GRAPH_SIZES = {
  smallScreenLive: {
    size: 250,
    box: {
      padding: '0.1rem',
    },
    title: {
      fontSize: '1rem',
      lineHeight: '0',
    },
    button: {
      height: '1rem',
      fontSize: '0.75rem',
    },
    stack: {
      marginBottom: '.2rem',
    },
  },
  live: {
    size: 300,
    box: {
      padding: '1.5rem',
    },
    title: {
      fontSize: '1.25rem',
    },
  },
  historical: {
    size: 450,
    box: {
      padding: '1.5rem',
    },
    title: {
      fontSize: '1.25rem',
    },
  },
};
